import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TenantLoginPage from './TenantLoginPage';
import TenantMainApp from './TenantMainApp';
import TenantSuspendedPage from './TenantSuspendedPage';
import NotificationPrompt from '../components/NotificationPrompt';
import IosPushHint from '../components/IosPushHint';
import { toast } from 'sonner';
import { controllaSessione } from '../utils/sessioneTenant';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTROLLO_SESSIONE_MS = 10 * 60 * 1000;

const TenantApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantDb, setTenantDb] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendedTenantName, setSuspendedTenantName] = useState('');
  const [blockMessage, setBlockMessage] = useState('');
  const [licenzaAvviso, setLicenzaAvviso] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // Configura PWA manifest per tenant
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/manifest-tenant.json';
    }
    
    // Cambia theme-color
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = '#06b6d4';
    }
    
    // Cambia titolo pagina
    document.title = 'Il Mio Ristorante - Ristorantissimo';
  }, []);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('tenant_token');
    const savedUser = localStorage.getItem('tenant_user');
    const savedTenantList = localStorage.getItem('tenant_list');
    
    if (token && savedUser) {
      try {
        const userObj = JSON.parse(savedUser);
        const tenantList = JSON.parse(savedTenantList || '[]');
        
        // Trova il tenant di default
        const defaultTenant = tenantList.find(t => t.is_default) || tenantList[0];
        
        // Controlla se il tenant è sospeso
        if (defaultTenant && defaultTenant.status === 'suspended') {
          setIsSuspended(true);
          setSuspendedTenantName(defaultTenant.nome || defaultTenant.name || '');
          setLoading(false);
          return;
        }
        if (defaultTenant) {
          setTenantDb(defaultTenant.database_name);
          // Salva tenant_db nel localStorage per componenti che ne hanno bisogno
          localStorage.setItem('tenant_db', defaultTenant.database_name);
          // L'interceptor in axiosConfig.js gestisce automaticamente gli header
        }
        
        setUser(userObj);
      } catch (e) {
        console.error('Error parsing user data:', e);
        localStorage.removeItem('tenant_token');
        localStorage.removeItem('tenant_user');
        localStorage.removeItem('tenant_list');
      }
    }
    setLoading(false);
  }, []);

  // Controllo LICENZA: blocco immediato su 403 LICENZA_BLOCCATA (anche a sessione attiva)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.codice === 'LICENZA_BLOCCATA') {
          setBlockMessage(error.response.data.detail || '');
          setIsSuspended(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Accesso: si rinnova da solo mentre l'app è in uso (apertura, ritorno sull'app, ogni 10 minuti)
  useEffect(() => {
    if (!user) return undefined;
    const alRitorno = () => { if (document.visibilityState === 'visible') controllaSessione(); };
    const scaduta = () => {
      handleLogout();
      // dopo che la pagina di accesso (con il suo Toaster) è comparsa
      setTimeout(() => toast.info('Sessione scaduta: accedi di nuovo'), 400);
    };
    controllaSessione();
    const timer = setInterval(controllaSessione, CONTROLLO_SESSIONE_MS);
    document.addEventListener('visibilitychange', alRitorno);
    window.addEventListener('sessione-scaduta', scaduta);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', alRitorno);
      window.removeEventListener('sessione-scaduta', scaduta);
    };
  }, [user]);

  // Controllo LICENZA: verifica periodica stato abbonamento (blocco + banner scadenza)
  useEffect(() => {
    if (!user || !tenantDb) return;
    let cancelled = false;
    const controllaLicenza = async () => {
      try {
        const res = await axios.get(`${API}/saas/tenant-status`, { params: { tenant: tenantDb } });
        if (cancelled || !res.data?.found) return;
        if (res.data.bloccato) {
          setSuspendedTenantName(res.data.nome || '');
          setBlockMessage(res.data.motivo || '');
          setIsSuspended(true);
        } else if (res.data.in_scadenza) {
          setLicenzaAvviso(res.data);
        } else {
          setLicenzaAvviso(null);
        }
      } catch (e) {
        // silente: non bloccare per errori di rete
      }
    };
    controllaLicenza();
    const timer = setInterval(controllaLicenza, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user, tenantDb]);

  const handleLogin = (userData) => {
    // Configura il tenant DB - l'interceptor gestisce gli header automaticamente
    const tenantList = userData.tenants || [];
    const defaultTenant = tenantList.find(t => t.is_default) || tenantList[0];
    
    // Controlla se il tenant è sospeso
    if (defaultTenant && defaultTenant.status === 'suspended') {
      setIsSuspended(true);
      setSuspendedTenantName(defaultTenant.nome || defaultTenant.name || '');
      return;
    }
    
    if (defaultTenant) {
      setTenantDb(defaultTenant.database_name);
      // Salva tenant_db nel localStorage per componenti che ne hanno bisogno
      localStorage.setItem('tenant_db', defaultTenant.database_name);
    }
    
    setUser(userData);
    
    // Mostra il prompt per le notifiche dopo 2 secondi dal login
    setTimeout(() => {
      setShowNotificationPrompt(true);
    }, 2000);
  };

  const handleLogout = () => {
    // Pulisci tutto il localStorage tenant
    localStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_refresh_token');
    localStorage.removeItem('tenant_user');
    localStorage.removeItem('tenant_list');
    localStorage.removeItem('tenant_db');
    // Pulisci eventuali header axios residui
    delete axios.defaults.headers.common['X-Tenant-DB'];
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setTenantDb(null);
    setIsSuspended(false);
    setSuspendedTenantName('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  // Se il tenant è sospeso o l'abbonamento è scaduto, blocca il sistema
  if (isSuspended) {
    return <TenantSuspendedPage tenantName={suspendedTenantName} message={blockMessage} />;
  }

  if (!user) {
    return <TenantLoginPage onLogin={handleLogin} />;
  }

  return (
    <>
      {licenzaAvviso && (
        <div data-testid="banner-abbonamento-scadenza" className="bg-amber-500 text-white text-sm font-semibold px-4 py-2 text-center sticky top-0 z-[60]">
          ⚠️ Abbonamento in scadenza il {licenzaAvviso.data_scadenza ? new Date(licenzaAvviso.data_scadenza).toLocaleDateString('it-IT') : ''}
          {licenzaAvviso.giorni_rimanenti != null ? ` (${licenzaAvviso.giorni_rimanenti === 0 ? 'oggi' : `${licenzaAvviso.giorni_rimanenti} giorni rimanenti`})` : ''} — contatta l'amministratore per il rinnovo
        </div>
      )}
      <TenantMainApp user={user} tenantDb={tenantDb} onLogout={handleLogout} />
      <NotificationPrompt 
        show={showNotificationPrompt} 
        onClose={() => setShowNotificationPrompt(false)}
        tenantId={(tenantDb || '').replace(/^(tenant_)+/, '')}
      />
      <IosPushHint />
    </>
  );
};

export default TenantApp;
